import { createSlice } from '@reduxjs/toolkit';
import { GlobalSliceType } from './types';

const initialState: GlobalSliceType = {
    selectedContextId: null,
    selectedOutputVersion: null,
    isRightSidebarEditorOpened: false,
    isRightSideBarOpened: false,
    openRightSidebarAutomatically: false,
    copiedValue: '',
    streamingValues: {},
    editorSelectedOutput: '',
    isProductTourFinished: true,
    additionalSettings: {
        ANONYMIZED: {
            yes: '',
            no: '',
        },
        TONES_OF_VOICE: [],
        FORMALITY_MARKS: [],
        URGENCY_MARKS: [],
        LENGTH_MARKS: [],
        STRUCTURES: [],
    },
    selectedOutputLanguages: [],
    messageOutputLanguages: [],
    userSignature: {},
    outputLanguages: [],
    isSignatureChecked: true,
    targetCountries: [],
    conversationGroups: [],
    showTour: false,
    instructionHistory: [],
    jobOrderData: null,
    instructionSuggestions: [],
    isPrivacyPolicyModalOpen: false,
};

export const globalSlice = createSlice({
    name: 'globalSlice',
    initialState,
    reducers: {
        setSelectedContextId: (state, action) => {
            state.selectedContextId = action.payload;
        },
        setSelectedOutputVersion: (state, action) => {
            state.selectedOutputVersion = action.payload;
        },
        onOpenEditorFromOutput: (state, action) => {
            state.selectedOutputVersion = action.payload;
            state.isRightSidebarEditorOpened = true;
            state.isRightSideBarOpened = true;
        },
        onOpenEditorFromCopy: (state, action) => {
            state.copiedValue = action.payload;
            state.openRightSidebarAutomatically = false;
            state.isRightSidebarEditorOpened = true;
            state.isRightSideBarOpened = true;
        },
        triggerRightSidebarEditor: (state) => {
            state.isRightSidebarEditorOpened = !state.isRightSidebarEditorOpened;
        },
        openRightSidebar: (state) => {
            state.isRightSideBarOpened = true;
            state.openRightSidebarAutomatically = false;
        },
        closeRightSidebar: (state) => {
            state.isRightSideBarOpened = false;
            state.openRightSidebarAutomatically = false;
        },
        onOpenRightSidebarAutomatically: (state) => {
            state.openRightSidebarAutomatically = true;
        },
        closeRightSidebarAutomatically: (state) => {
            state.openRightSidebarAutomatically = false;
        },
        clearStreaming: (state, action) => {
            const data = { ...state.streamingValues };
            delete data[action.payload.jobId];
            state.streamingValues = { ...data };
        },
        setStreamingValues: (state, action) => {
            const value = action.payload?.isStart
                ? ''
                : (state.streamingValues[action.payload.jobId]?.value ?? '') + action.payload.value;

            state.streamingValues = {
                ...state.streamingValues,
                [action.payload.jobId]: {
                    value,
                    done:
                        action.payload.done ??
                        state.streamingValues[action.payload.jobId]?.done ??
                        false,
                },
            };
        },
        setEditorSelectedOutput: (state, action) => {
            state.editorSelectedOutput = action.payload;
        },
        setIsProductTourFinished: (state, action) => {
            state.isProductTourFinished = action.payload;
        },
        setAdditionalSettings: (state, action) => {
            state.additionalSettings = action.payload;
        },
        setSelectedOutputLanguages: (state, action) => {
            state.selectedOutputLanguages = action.payload;
        },
        setMessageOutputLanguages: (state, action) => {
            const message = state.messageOutputLanguages.find(
                (item) => item.messageId === action.payload.messageId,
            );
            if (message) {
                return {
                    ...state,
                    messageOutputLanguages: [
                        ...state.messageOutputLanguages.filter(
                            (item) => item.messageId !== message.messageId,
                        ),
                        action.payload,
                    ],
                };
            }
            return {
                ...state,
                messageOutputLanguages: [...state.messageOutputLanguages, action.payload],
            };
        },
        setUserSignature: (state, action) => {
            state.userSignature = action.payload;
        },
        setOutputLanguages: (state, action) => {
            state.outputLanguages = action.payload;
        },
        setIsSignatureChecked: (state, action) => {
            state.isSignatureChecked = action.payload;
        },
        setTargetCountries: (state, action) => {
            state.targetCountries = action.payload;
        },
        setConversationGroups: (state, action) => {
            return {
                ...state,
                conversationGroups: action.payload,
            };
        },
        updateConversationGroup: (state, action) => {
            return {
                ...state,
                conversationGroups: state.conversationGroups.map((g) =>
                    g.id === action.payload.id ? action.payload : g,
                ),
            };
        },
        addGroupToConversation: (state, action) => {
            const groupExist = state.conversationGroups.find((g) => g.id === action.payload.id);
            if (groupExist) {
                return { ...state };
            }
            return {
                ...state,
                conversationGroups: [...state.conversationGroups, action.payload],
            };
        },
        setShowTour: (state, action) => {
            state.showTour = action.payload;
        },
        setInstructionHistory: (state, action) => {
            state.instructionHistory = action.payload;
        },
        setJobOrderData: (state, action) => {
            state.jobOrderData = action.payload;
        },
        updateJobOrderData: (state, action) => {
            if (state.jobOrderData) {
                state.jobOrderData = { ...state.jobOrderData, ...action.payload };
            } else {
                state.jobOrderData = action.payload;
            }
        },
        clearJobOrderData: (state) => {
            state.jobOrderData = null;
        },
        setInstructionSuggestions: (state, action) => {
            state.instructionSuggestions = action.payload;
        },
        setPrivacyPolicyModalOpen: (state, action) => {
            state.isPrivacyPolicyModalOpen = action.payload;
        },
    },
});

export const {
    setSelectedContextId,
    onOpenEditorFromOutput,
    triggerRightSidebarEditor,
    openRightSidebar,
    closeRightSidebar,
    setSelectedOutputVersion,
    onOpenRightSidebarAutomatically,
    closeRightSidebarAutomatically,
    onOpenEditorFromCopy,
    setStreamingValues,
    clearStreaming,
    setEditorSelectedOutput,
    setIsProductTourFinished,
    setAdditionalSettings,
    setSelectedOutputLanguages,
    setMessageOutputLanguages,
    setUserSignature,
    setOutputLanguages,
    setIsSignatureChecked,
    setTargetCountries,
    setConversationGroups,
    updateConversationGroup,
    addGroupToConversation,
    setShowTour,
    setInstructionHistory,
    setJobOrderData,
    updateJobOrderData,
    clearJobOrderData,
    setInstructionSuggestions,
    setPrivacyPolicyModalOpen,
} = globalSlice.actions;

export default globalSlice.reducer;
